import { api, type Deposit, queryKeys } from '@alpha/api-client';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { useTableQuery } from '../../hooks/useTableQuery';
import { formatDateTime, formatLds, formatUsd, statusTone } from '../../lib/format';
import { Chip } from '../../ui';
import { DEPOSIT_TABS } from './DepositList';

const col = createColumnHelper<Deposit>();

/** On-chain (DApp) deposits picked up by the chain watcher. */
export function BlockchainDeposits() {
  const { query, setPage } = useTableQuery();

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.deposits({ scope: 'blockchain', ...query }),
    queryFn: () => api.deposits.adminBlockchain(query),
  });

  const columns = useMemo(
    () => [
      col.accessor('user_code', { header: 'User ID', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('chain', { header: 'Chain', cell: (i) => i.getValue() ?? 'BSC' }),
      col.accessor('from_address', {
        header: 'From',
        cell: (i) => (i.getValue() ? `${i.getValue()!.slice(0, 8)}…${i.getValue()!.slice(-6)}` : '—'),
      }),
      col.accessor('tx_hash', {
        header: 'Tx Hash',
        cell: (i) => (i.getValue() ? `${i.getValue()!.slice(0, 12)}…` : '—'),
      }),
      col.accessor('amount_lds', { header: 'LDS', cell: (i) => formatLds(i.getValue()) }),
      col.accessor('amount_usd', { header: 'USD', cell: (i) => formatUsd(i.getValue()) }),
      col.accessor('status', {
        header: 'Status',
        cell: (i) => <Chip tone={statusTone(i.getValue())}>{i.getValue()}</Chip>,
      }),
      col.accessor('created_at', { header: 'Detected', cell: (i) => formatDateTime(i.getValue()) }),
    ],
    [],
  );

  return (
    <>
      <PageHeader title="Deposits" description="On-chain deposits detected by the DApp watcher" tabs={DEPOSIT_TABS} />
      <DataTable<Deposit>
        title="Blockchain Deposits"
        columns={columns}
        data={data?.data}
        meta={data?.meta}
        isLoading={isLoading}
        error={error as Error | null}
        onPageChange={setPage}
      />
    </>
  );
}
